import { createEvent } from './event'
import isInViewDistance from './is-in-view-distance'
import logger from './logger'
import request from './request'
/**
 * Creates and injects an iframe into the given HTML node.
 *
 * The technique used is inspired by the following two articles:
 * - http://www.aaronpeters.nl/blog/iframe-loading-techniques-performance#dynamic
 * - http://www.lognormal.com/blog/2012/12/12/the-script-loader-pattern
 *
 * @param {Window} window A reference to the main frame's window.
 * @param {Object} config A config object specifying the iframe's data attributes.
 * @param {HTMLElement} node The HTML element the iframe is appended to.
 * @param {Function} callback A callback function executed
 * @param {Object} widgetConfig An object that will be injected into the widget iframe.
 * @returns {HTMLIFrameElement} A reference to the created iframe.
 */
function injectIframe(window, config, node, callback, widgetConfig) {
  var iframe = window.document.createElement('iframe')
  iframe.src = 'javascript:void(0)'
  iframe.title = '8SELECT product recommendations'
  iframe.role = 'presentation'
  iframe.style.cssText = config.cssText || 'width: 100%; height: 500px; border: 0; overflow: hidden;'

  iframe.frameborder = '0' // HTML 4 only
  iframe.scrolling = 'no' // HTML 4 only
  node.appendChild(iframe)

  /**
   * Explicitely set document.domain if was also set explicitely in main frame.
   */
  var iframeDocument, iframeDomain
  try {
    iframeDocument = iframe.contentWindow.document
  } catch (exception) {
    iframeDomain = document.domain
    iframe.src = 'javascript:var d=document.open();d.domain="' + iframeDomain + '";void(0);'
    iframeDocument = iframe.contentWindow.document
  }

  /**
   * Attaches a data attribute to an HTML element for each key-value-pair in a given object.
   *
   * @param {string} html The plain HTML document as text.
   * @param {Object} data The object to translate into data attributes.
   * @return {string} The plain HTML document as text including data-attributes for <body>.
   */
  function attachDataAttributes(html, data) {
    const attributes = Object.keys(data)
      .map(key => `data-${key}="${data[key]}"`)
      .join(' ')

    var replacement = '</head><body ' + attributes

    return html.replace(/<\/head>[\n\s\t\r]*<body/, replacement)
  }

  const xhrCallback = function(error, xhr) {
    if (error) {
      return callback(error)
    }

    try {
      iframe.contentWindow.widgetConfig = widgetConfig

      const htmlWithAttributes = attachDataAttributes(xhr.response, config)

      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlWithAttributes, 'text/html')
      iframeDocument.removeChild(iframeDocument.body.parentNode)
      iframeDocument.appendChild(doc.body.parentNode)

      Object.defineProperty(iframeDocument, 'readyState', { value: 'loading', configurable: true })

      const scripts = iframeDocument.querySelectorAll('script')
      const loadScriptsSync = (scripts, index) => {
        if (index > scripts.length - 1) {
          const event = iframeDocument.createEvent('Event')
          event.initEvent('DOMContentLoaded', true, true)
          iframeDocument.dispatchEvent(event)
          return Object.defineProperty(iframeDocument, 'readyState', { value: 'complete', configurable: true })
        }
        const script = scripts[index]
        const newScript = iframeDocument.createElement('script')
        if (script.src) {
          newScript.onload = () => loadScriptsSync(scripts, index + 1)
          newScript.onError = newScript.onload
          newScript.src = script.src
        }
        if (script.innerHTML) {
          newScript.appendChild(document.createTextNode(script.innerHTML))
        }

        script.parentNode.appendChild(newScript)
        script.parentNode.removeChild(script)

        if (script.innerHTML) {
          loadScriptsSync(scripts, index + 1)
        }
      }
      loadScriptsSync(scripts, 0)

      const links = iframeDocument.querySelectorAll('link')
      for (let j = 0; j < links.length; ++j) {
        const link = links[j]
        const newLink = iframeDocument.createElement('link')
        newLink.rel = link.rel
        newLink.href = link.href
        link.parentNode.appendChild(newLink)
        link.parentNode.removeChild(link)
      }

      iframeDocument.body.style.display = config.display || 'table'
      iframeDocument.body.style.width = '100%'

      callback(null, iframe)
    } catch (error) {
      logger('Something went wrong during iframe injection.', { error })
      return callback(error)
    }
  }

  const initIframeLoading = (iframeDocument, config) => {
    const debounceLoading = window._8select.config.lazyloading
      ? isInViewDistance(config.uuid, config.loadDistanceFactor) === false
      : false

    if (debounceLoading) {
      return setTimeout(() => initIframeLoading(iframeDocument, config), 300)
    }

    if (widgetConfig && widgetConfig.getSysPromise) {
      widgetConfig.sysPromise = widgetConfig.getSysPromise()
    }

    const event = createEvent({ type: 'initDocumentSwap' })
    iframeDocument.dispatchEvent(event)
  }

  iframeDocument.open()
  iframeDocument.initDocumentSwap = request.bind(undefined, config.url, 'GET', undefined, xhrCallback)

  iframeDocument.addEventListener('initDocumentSwap', iframeDocument.initDocumentSwap, { once: true })

  iframeDocument.close()
  setTimeout(() => initIframeLoading(iframeDocument, config))

  return iframe
}

export default injectIframe
